import { useEventNameMatch } from '@shared/components/AuthProvider/AuthProvider.utils';
import { get } from 'lodash-es';
import { useTranslation } from '@shared/core';
import { EventScheduleItemStreamFragment, useGetEventSessionQuery, useGuestCanGetAccessEventByNameActionBarQuery, useLogoutEventSessionMutation } from '@graphql/generated';
import { useUnlockDialogContext } from '@apps/guest/packages/layout-engine/components/UnlockDialog/UnlockDialogProvider';
import { performFullPageRedirect } from '@shared/utils/navigation';
import { useCallback, useRef, useState } from 'react';
import { useOnIntersectingChange } from '@shared/utils/hooks/useOnIntersectingChange';
import { isStartDaysBefore, isStartMinutesBefore } from '@apps/guest/packages/layout-engine/components/VirtualEventBlock/VirtualEventBlock.utils';
import { isPast } from 'date-fns';
import { useRouteMatch } from '@react-router';
import { useLayout } from '@apps/guest/packages/layout-engine/layouts/LayoutProvider';
import { useActionBarTelemetry } from './ActionBar.telemetry';

interface ActionBarControllerProps {
  rsvpTitle: Maybe<string>;
}

export const useActionBarController = ({ rsvpTitle }: ActionBarControllerProps) => {
  const { onOpen } = useUnlockDialogContext();
  const [isTriggerPoint, setIsTriggerPoint] = useState(false);
  const triggerPointRef = useRef(null);
  const nameMatchResult = useEventNameMatch();
  const eventHandle = get(nameMatchResult, 'eventName', '');
  const { data } = useGuestCanGetAccessEventByNameActionBarQuery({ variables: { eventHandle }, batchMode: 'fast', ssr: false });
  const { data: sessionData } = useGetEventSessionQuery({ variables: { eventHandle }, batchMode: 'fast', ssr: false });
  const [logoutEvent] = useLogoutEventSessionMutation({ variables: { eventId: data?.eventByName?.id } });
  const nowDate = Date.now();
  const telemetry = useActionBarTelemetry();

  //Livestream variables parsing
  const streamNow = get(data, 'eventByName.info.schedule.virtualScheduleItemNow', null);
  const scheduleItems = get(data, 'eventByName.info.schedule.items', []);

  const streamToDisplay =
    streamNow ||
    scheduleItems.filter((item: EventScheduleItemStreamFragment) => !!item.virtualEventLink && isStartMinutesBefore(item.startTime?.milliseconds || 0, nowDate, 15))[0];

  const streamEvent = get(streamToDisplay, 'virtualEventLink', null);
  const streamStartTime = get(streamToDisplay, 'startTime', null);
  const streamStartTimeMs = get(streamToDisplay, 'startTime.milliseconds', 0);
  const streamEndTime = get(streamToDisplay, 'endTime', null);
  const streamEndTimeMs = get(streamToDisplay, 'endTime.milliseconds', 0);
  const resolvedEndTimeMs = streamEndTimeMs || streamStartTimeMs + 1000 * 60 * 60;
  const isEventPast = isPast(resolvedEndTimeMs);

  //Rest Event variables parsing
  const date = get(data, 'eventByName.info.dateV0918.dateString', '').split(' ').slice(0, -2).join(' ');
  const dateInMilliseconds = get(data, 'eventByName.info.dateV0918.milliseconds', null);
  const hasUnlocked = sessionData?.eventByName?.activeSession.hasUnlocked ?? false;
  const hasIdentity = sessionData?.eventByName?.activeSession.hasIdentity ?? false;
  const firstName = sessionData?.eventByName?.activeSession.person?.firstName ?? '';
  const lastName = sessionData?.eventByName?.activeSession.person?.lastName ?? '';
  const guestName = [firstName, lastName].filter(name => !!name).join(' ');
  const rsvpIsEnabled = sessionData?.eventByName?.activeSession.eventCTA.rsvpIsEnabled ?? false;
  const rsvpIsCompleted = sessionData?.eventByName?.activeSession.eventCTA.rsvpIsCompleted ?? false;
  const isRsvpRequiresPassword = sessionData?.eventByName?.activeSession?.eventCTA?.requiresPassword || false;
  const rsvpAttendanceStatus = sessionData?.eventByName?.activeSession?.eventCTA?.rsvpAttendanceStatus || 'notAttending';
  const isRequiredInvite = sessionData?.eventByName?.activeSession.eventCTA.scheduleHasInviteOnlyItems ?? false;
  const rsvpLabel = sessionData?.eventByName?.activeSession.eventCTA.label ?? '';
  const isRsvpUnlocked = !isRsvpRequiresPassword || hasUnlocked;
  const isReminderWarning = isStartDaysBefore(dateInMilliseconds, nowDate, 14); //when the event date is 2 weeks or less away

  //Text Translation
  const { t, t2 } = useTranslation('actionBar');
  const dateText = t('text', 'date')({ date });
  const personalizedText = t('text', 'personalizedText')({ guestName });
  const rsvpButtonTextDefault = t2('buttons', 'buttonRsvp');
  const buttonRsvp = rsvpAttendanceStatus === 'noAttendanceQuestion' && rsvpLabel === 'RSVP Here' ? rsvpButtonTextDefault : rsvpLabel;
  const { buttonUnlock, buttonNotYou, buttonFindInvite } = t2('buttons');

  const onLogoutHandle = () => {
    logoutEvent({ refetchQueries: () => ['GetEventSession', 'GetGuestsiteSchedule'] })
      .then(() => telemetry.onLogoutClicked())
      .catch(err => telemetry.trackError('ActionBarLogout', err));
  };

  const onVirtualEventBlockClicked = () => {
    telemetry.onVirtualEventClicked();
  };

  const onRsvpHandle = () => {
    performFullPageRedirect(`/${eventHandle}/rsvp`);
    telemetry.onRsvpHandle();
  };

  const onIntersectingTriggerPointEl = useCallback(
    (isIntersecting: boolean) => {
      if (isIntersecting && triggerPointRef.current) {
        setIsTriggerPoint(true);
      } else {
        setIsTriggerPoint(false);
      }
    },
    [setIsTriggerPoint]
  );

  useOnIntersectingChange(triggerPointRef, onIntersectingTriggerPointEl);

  const mainText = () => {
    if (guestName) return personalizedText;
    else if (date) return dateText;
    return undefined;
  };

  //General conditions
  const isStreamDisplayed = !!streamToDisplay && !isEventPast;
  const shouldDisplayFindInvite = isRequiredInvite && !hasIdentity;
  const isUnlockBtnDisplayed = !hasUnlocked || shouldDisplayFindInvite;
  const isRsvpDisplayed = rsvpIsEnabled && !rsvpIsCompleted && isRsvpUnlocked;

  //Display on Brannan Welcome only when Live
  const { layout } = useLayout();
  const isWelcomePage = !!useRouteMatch('/:eventHandle/welcome');
  const isNotLiveOnBrannanWelcome = layout === 'brannan' && isWelcomePage && !streamNow;

  return {
    isActionBarHidden: (hasUnlocked && !isRsvpDisplayed && !isStreamDisplayed && !shouldDisplayFindInvite) || isNotLiveOnBrannanWelcome,
    isTriggerPoint,
    triggerPointRef,
    isUnlockBtnDisplayed,
    isReminderWarning,
    buttonDialog: hasUnlocked ? buttonFindInvite : buttonUnlock,
    onUnlockDialogHandle: onOpen,
    buttonNotYou,
    onLogoutHandle,
    guestName,
    isRsvpDisplayed,
    buttonRsvp,
    onRsvpHandle,
    mainText: mainText(),
    isStreamDisplayed,
    streamStartTime,
    streamEndTime,
    streamEvent,
    streamNow,
    onVirtualEventBlockClicked
  };
};
